import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash';
import * as moment from 'moment';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { DialogService } from 'app/backpack/dialog-modal/dialog.service';
import { SchoolActivity, SchoolActivityInstance } from 'app/models';
import { FeatureFlagService, FLAGS } from 'app/services/feature-flag.service';
import { FlexPeriodService } from 'app/services/flex-period.service';
import { HttpService } from 'app/services/http-service';
import {
	CreateSchoolActivityInstanceReq,
	GetActivityInstancesReq,
	InstanceTimes,
	SchoolActivityService,
	UpdateSchoolActivityRequest,
} from 'app/services/school-activity.service';
import {
	activityScheduleFormFields,
	CUSTOM_SCHEDULE_DATE_KEY,
	CUSTOM_SCHEDULE_REPEAT_KEY,
	CUSTOM_SCHEDULE_TIME_RANGE_KEY,
	DEFAULT_REPEAT_END_DATE,
	FLEX_INSTANCE_TIMES_KEY,
	FLEX_PERIOD_ID_KEY,
	FLEX_PERIOD_REPEATS_KEY,
	getActivityStartDate,
	getActivityTimeRange,
	getRepeatOptions,
	getCustomStartEndTime,
	getUpcomingCustomInstanceTimes,
	getUpcomingFlexInstanceTimes,
	SCHEDULE_REPEAT_END_DATE_KEY,
	SCHEDULE_TYPE_KEY,
} from '../activity-form-util';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: '[sp-edit-activity-schedule-form]',
	host: {
		'[class.!tw-cursor-progress]': 'activityInstancesLoading$.value',
		'(click)': 'openEditScheduleModal()',
	},
	template: `
		<ng-content></ng-content>

		<ng-template #editScheduleModalBody let-dismiss>
			<bp-dialog-header>Edit Schedule</bp-dialog-header>
			<bp-dialog-body>
				<sp-activity-schedule-form
					[editScheduleForm]="editScheduleForm"
					[activity]="activity"
					[activityInstances]="activityInstances$"></sp-activity-schedule-form>
			</bp-dialog-body>
			<bp-dialog-footer>
				<bp-button (click)="dismiss()" type="white" title="Cancel"></bp-button>
				<bp-button (click)="saveScheduleChanges(dismiss)" type="color" title="Save" [disabled]="getSaveButtonState()"></bp-button>
			</bp-dialog-footer>

			<!-- BEGIN DESTRUCTIVE WARNING MODAL -->
			<ng-template #fullDestructionWarningModalBody let-dismissNested>
				<bp-dialog-header>Delete {{ activity.name }} sessions?</bp-dialog-header>
				<bp-dialog-body>
					Changing the activity's schedule will delete upcoming {{ activity.name }} sessions and create new ones according to the updated schedule.
					Students will need to be added again. This action can't be undone.
				</bp-dialog-body>
				<bp-dialog-footer>
					<bp-button (click)="dismissNested()" type="white" title="Cancel"></bp-button>
					<bp-button (click)="confirmScheduleChanges(dismissNested, dismiss)" type="danger" title="Delete Sessions"></bp-button>
				</bp-dialog-footer>
			</ng-template>

			<ng-template #partialDestructionWarningModalBody let-dismissNested>
				<bp-dialog-header>Delete {{ activity.name }} sessions?</bp-dialog-header>
				<bp-dialog-body>
					Changing the activity's schedule will delete some upcoming {{ activity.name }} sessions. Past sessions won't be affected. This action can't
					be undone.
				</bp-dialog-body>
				<bp-dialog-footer>
					<bp-button (click)="dismissNested()" type="white" title="Cancel"></bp-button>
					<bp-button (click)="confirmScheduleChanges(dismissNested, dismiss)" type="danger" title="Delete Sessions"></bp-button>
				</bp-dialog-footer>
			</ng-template>
			<!-- END DESTRUCTIVE WARNING MODAL -->
		</ng-template>
	`,
})
export class EditActivityScheduleFormComponent {
	@Input() activity!: SchoolActivity;
	@Input() canEdit = false;

	// Schedule Form
	@ViewChild('editScheduleModalBody', { read: TemplateRef, static: false }) editScheduleModalBody!: TemplateRef<unknown>;
	editScheduleForm = new FormGroup(activityScheduleFormFields);
	activityInstances$ = new BehaviorSubject<SchoolActivityInstance[]>([]);
	activityInstancesLoading$ = new BehaviorSubject<boolean>(false);

	// Destructive change warning
	@ViewChild('fullDestructionWarningModalBody', { read: TemplateRef, static: false }) fullDestructionWarningModalBody!: TemplateRef<unknown>;
	@ViewChild('partialDestructionWarningModalBody', { read: TemplateRef, static: false }) partialDestructionWarningModalBody!: TemplateRef<unknown>;

	constructor(
		private backpackDialogService: DialogService,
		private featureFlagService: FeatureFlagService,
		private httpService: HttpService,
		private schoolActivityService: SchoolActivityService,
		private flexPeriodService: FlexPeriodService,
		private cdr: ChangeDetectorRef
	) {}

	protected updateActivity(updateReq: Partial<UpdateSchoolActivityRequest>): void {
		this.schoolActivityService.UpdateActivity(updateReq, this.activity);
	}

	async openEditScheduleModal(): Promise<void> {
		if (!this.canEdit || this.activityInstancesLoading$.value) {
			return;
		}

		this.activityInstancesLoading$.next(true);

		// Reset form data when opening modal
		const tz = this.httpService.currentSchoolSubject.getValue().timezone;
		this.editScheduleForm.setValue({
			[FLEX_PERIOD_ID_KEY]: this.activity.flex_period_id,
			[FLEX_PERIOD_REPEATS_KEY]: this.activity.state,
			[FLEX_INSTANCE_TIMES_KEY]: [],
			[SCHEDULE_REPEAT_END_DATE_KEY]: this.activity.repeat_end_date
				? moment(this.activity.repeat_end_date)
				: this.featureFlagService.isFeatureEnabledV2(FLAGS.CustomActivitySchedule)
				? DEFAULT_REPEAT_END_DATE
				: null,
			[SCHEDULE_TYPE_KEY]: this.activity.schedule_type,
			[CUSTOM_SCHEDULE_DATE_KEY]: getActivityStartDate(this.activity, tz),
			[CUSTOM_SCHEDULE_TIME_RANGE_KEY]: getActivityTimeRange(this.activity, tz),
			[CUSTOM_SCHEDULE_REPEAT_KEY]: this.activity.custom_repeat_rule ?? '',
		});

		const startDate = new Date();
		const endDate = this.activity.repeat_end_date ? moment(this.activity.repeat_end_date).toDate() : DEFAULT_REPEAT_END_DATE.toDate();

		const req: GetActivityInstancesReq = {
			start_time: startDate,
			end_time: endDate,
			activity_id: this.activity.id,
		};

		try {
			// Refresh instances when opening schedule modal
			const instances = await firstValueFrom(this.schoolActivityService.GetActivityInstancesHTTP(req));
			this.activityInstances$.next(instances);

			this.backpackDialogService.openDialog(this.editScheduleModalBody, this.cdr);
		} finally {
			this.activityInstancesLoading$.next(false);
		}
	}

	protected getSaveButtonState(): boolean {
		const formValue = this.editScheduleForm.value;
		const scheduleType = formValue[SCHEDULE_TYPE_KEY];

		let disablePrimaryButton = true;

		// Check for data differences
		const sameEndDate = this.featureFlagService.isFeatureEnabledV2(FLAGS.CustomActivitySchedule)
			? this.activity.repeat_end_date
				? moment(formValue[SCHEDULE_REPEAT_END_DATE_KEY]).isSame(moment(this.activity.repeat_end_date), 'minute')
				: true
			: true;

		if (scheduleType != this.activity.schedule_type) {
			disablePrimaryButton = false;
		} else if (scheduleType === 'flex_period') {
			const sameFlexPeriod = formValue[FLEX_PERIOD_ID_KEY] === this.activity.flex_period_id;
			const sameRepeats = formValue[FLEX_PERIOD_REPEATS_KEY] === this.activity.state;
			const noNewInstances = formValue[FLEX_INSTANCE_TIMES_KEY] ? formValue[FLEX_INSTANCE_TIMES_KEY].length === 0 : true;

			disablePrimaryButton = sameFlexPeriod && sameRepeats && noNewInstances && sameEndDate;
		} else {
			const tz = this.httpService.currentSchoolSubject.getValue().timezone;
			const sameDate = getActivityStartDate(this.activity, tz).isSame(formValue[CUSTOM_SCHEDULE_DATE_KEY]);
			const sameTime = isEqual(formValue[CUSTOM_SCHEDULE_TIME_RANGE_KEY], getActivityTimeRange(this.activity, tz));
			const sameRRule = isEqual(formValue[CUSTOM_SCHEDULE_REPEAT_KEY], this.activity.custom_repeat_rule);

			disablePrimaryButton = sameDate && sameTime && sameRRule && sameEndDate;
		}

		// Check for invalid fields
		const validEndDate = this.featureFlagService.isFeatureEnabledV2(FLAGS.CustomActivitySchedule)
			? this.editScheduleForm.get(SCHEDULE_REPEAT_END_DATE_KEY)?.valid
			: true;

		disablePrimaryButton = disablePrimaryButton || !validEndDate;

		return disablePrimaryButton;
	}

	protected saveScheduleChanges(dismiss: () => void, confirmedDestructiveChanges = false): void {
		const scheduleType = this.editScheduleForm.value[SCHEDULE_TYPE_KEY];
		const scheduleTypeChanged = scheduleType !== this.activity.schedule_type;
		const updateReq: Partial<UpdateSchoolActivityRequest> = {
			id: this.activity.id,
			schedule_type: scheduleType,
		};

		const dangerousChanges = this.hasDangerousChanges();
		if (dangerousChanges != 'none') {
			if (!confirmedDestructiveChanges) {
				this.backpackDialogService.openDialog(
					dangerousChanges == 'full' ? this.fullDestructionWarningModalBody : this.partialDestructionWarningModalBody,
					this.cdr
				);
				return;
			}
		}

		if (scheduleType === 'flex_period') {
			void this.handleFlexPeriodSave(updateReq, scheduleTypeChanged);
		} else {
			void this.handleCustomScheduleSave(updateReq, scheduleTypeChanged);
		}
		dismiss();
	}

	private async handleFlexPeriodSave(req: Partial<UpdateSchoolActivityRequest>, scheduleTypeChanged: boolean): Promise<void> {
		if (!this.editScheduleForm.value[FLEX_PERIOD_ID_KEY] || !this.editScheduleForm.value[FLEX_PERIOD_REPEATS_KEY]) {
			return;
		}

		req.flex_period_id = this.editScheduleForm.value[FLEX_PERIOD_ID_KEY];
		req.state = this.editScheduleForm.value[FLEX_PERIOD_REPEATS_KEY];
		req.repeat_end_date = req.state == 'flex_recurring' ? this.editScheduleForm.value[SCHEDULE_REPEAT_END_DATE_KEY]?.toISOString() : null;

		if (scheduleTypeChanged) {
			req.custom_start_time = null;
			req.custom_end_time = null;
			req.custom_repeat_rule = '';
		}

		const prevActivity: SchoolActivity = { ...this.activity };
		this.updateActivity(req);

		const instances: InstanceTimes[] = this.editScheduleForm.value[FLEX_INSTANCE_TIMES_KEY] ?? [];

		/**
		 * Deleting instances
		 *
		 * Any instances that don't match the new schedule will be deleted.
		 */
		if (prevActivity.flex_period_id !== req.flex_period_id) {
			// Delete all existing instances when flex period changes
			await this.deleteActivityInstanceTimes(this.activityInstances$.value);
		} else if (prevActivity.state === 'flex_recurring' && req.state === 'scheduled') {
			// Delete existing instances not in the selected days
			const toDelete = this.activityInstances$.value.filter(
				(existing) => !instances.find((newTime) => moment(newTime.start).isSame(moment(existing.start_time)))
			);
			await this.deleteActivityInstanceTimes(toDelete);
		} else if (req.state === 'flex_recurring' && this.changedEndDate(this.activity, req)) {
			// Delete instances past the end of the new end date
			await this.deleteInstancesAfter(this.activityInstances$.value, req.repeat_end_date);
		}

		const flexPeriod = await firstValueFrom(this.flexPeriodService.flexPeriod$(req.flex_period_id));
		if (!flexPeriod) {
			return;
		}

		/**
		 * Create new scheduled instances
		 */
		const tz = this.httpService.currentSchoolSubject.getValue().timezone;
		let newInstances: InstanceTimes[] = [];
		if (!prevActivity.id || prevActivity.flex_period_id !== req.flex_period_id) {
			newInstances = getUpcomingFlexInstanceTimes(flexPeriod, new Date(), tz, req.repeat_end_date);
		} else if (req.state === 'scheduled') {
			// Only create instances that do not already exist
			newInstances = instances.filter(
				(newTime) => !this.activityInstances$.value.find((existing) => moment(existing.start_time).isSame(moment(newTime.start)))
			);
		} else if (req.state === 'flex_recurring') {
			// Create instances past the end of the old end date
			newInstances = getUpcomingFlexInstanceTimes(flexPeriod, new Date(), tz, req.repeat_end_date).filter(
				(newTime) => !this.activityInstances$.value.find((existing) => moment(existing.start_time).isSame(moment(newTime.start)))
			);
		}
		this.updateActivityInstanceTimes(newInstances);
	}

	private async handleCustomScheduleSave(req: Partial<UpdateSchoolActivityRequest>, scheduleTypeChanged: boolean): Promise<void> {
		if (!this.editScheduleForm.value[CUSTOM_SCHEDULE_DATE_KEY] || !this.editScheduleForm.value[CUSTOM_SCHEDULE_TIME_RANGE_KEY]) {
			return;
		}

		const tz = this.httpService.currentSchoolSubject.getValue().timezone;
		const { startTime, endTime } = getCustomStartEndTime(
			this.editScheduleForm.value[CUSTOM_SCHEDULE_DATE_KEY],
			this.editScheduleForm.value[CUSTOM_SCHEDULE_TIME_RANGE_KEY],
			tz
		);

		req.custom_start_time = startTime.toISOString();
		req.custom_end_time = endTime.toISOString();
		req.custom_repeat_rule = this.editScheduleForm.value[CUSTOM_SCHEDULE_REPEAT_KEY];
		req.repeat_end_date = req.custom_repeat_rule ? this.editScheduleForm.value[SCHEDULE_REPEAT_END_DATE_KEY]?.toISOString() : null;

		if (scheduleTypeChanged) {
			req.flex_period_id = -1;
		}

		this.updateActivity(req);

		let newInstances: InstanceTimes[] = [];
		if (!this.activity.id) {
			// New activity - create all instances
			newInstances = getUpcomingCustomInstanceTimes(req.custom_start_time, req.custom_end_time, req.custom_repeat_rule, req.repeat_end_date);
		} else if (scheduleTypeChanged || this.customScheduleNeedsToBeReset(this.activity, req)) {
			// Delete instances that don't match the new schedule, create any that need to be created
			const fullSchedule = getUpcomingCustomInstanceTimes(req.custom_start_time, req.custom_end_time, req.custom_repeat_rule, req.repeat_end_date);
			const nonMatchingInstances = this.activityInstances$.value.filter(
				(existing) => !fullSchedule.find((newTime) => moment(newTime.start).isSame(moment(existing.start_time), 'minute'))
			);
			newInstances = fullSchedule.filter(
				(newTime) => !this.activityInstances$.value.find((existing) => moment(existing.start_time).isSame(moment(newTime.start), 'minute'))
			);
			await this.deleteActivityInstanceTimes(nonMatchingInstances);
		} else if (this.changedEndDate(this.activity, req)) {
			await this.deleteInstancesAfter(this.activityInstances$.value, req.repeat_end_date);

			if (this.activity.repeat_end_date) {
				const startTimeMoment = moment(req.custom_start_time);
				const startTimeIso = moment(this.activity.repeat_end_date)
					.add(1, 'day')
					.set({ hour: startTimeMoment.hour(), minute: startTimeMoment.minute(), second: 0, millisecond: 0 })
					.toISOString();
				const endTimeMoment = moment(req.custom_end_time);
				const endTimeIso = moment(this.activity.repeat_end_date)
					.add(1, 'day')
					.set({ hour: endTimeMoment.hour(), minute: endTimeMoment.minute(), second: 0, millisecond: 0 })
					.toISOString();

				newInstances = getUpcomingCustomInstanceTimes(startTimeIso, endTimeIso, req.custom_repeat_rule, req.repeat_end_date);
			}
		}
		this.updateActivityInstanceTimes(newInstances);
	}

	private async deleteInstancesAfter(instances: SchoolActivityInstance[], endDateIso: string | null | undefined): Promise<void> {
		if (!endDateIso) {
			return;
		}

		const endDate = moment(endDateIso);

		const instancesToDelete = instances.filter((instance) => moment(instance.start_time).isAfter(endDate));
		if (instancesToDelete.length === 0) {
			return;
		}

		await this.deleteActivityInstanceTimes(instancesToDelete);
	}

	private updateActivityInstanceTimes(instances: InstanceTimes[]): void {
		if (instances.length === 0) {
			return;
		}

		const reqs: CreateSchoolActivityInstanceReq[] = instances.map((time) => {
			return { start: time.start, end: time.end, activityId: this.activity.id, state: 'scheduled', selected: false };
		});
		this.schoolActivityService.CreateActivityInstanceBulk(reqs);
	}

	private async deleteActivityInstanceTimes(instances: SchoolActivityInstance[]): Promise<void> {
		await this.schoolActivityService.DeleteActivityInstanceBulk(
			this.activity.id,
			instances.map((instance) => instance.id)
		);
	}

	private customScheduleNeedsToBeReset(activity: SchoolActivity, req: Partial<UpdateSchoolActivityRequest>): boolean {
		return (
			!moment(activity.custom_start_time).isSame(moment(req.custom_start_time)) ||
			!moment(activity.custom_end_time).isSame(moment(req.custom_end_time)) ||
			activity.custom_repeat_rule !== req.custom_repeat_rule
		);
	}

	private changedEndDate(activity: SchoolActivity, req: Partial<UpdateSchoolActivityRequest>): boolean {
		if (!activity.repeat_end_date && req.repeat_end_date) {
			return true;
		}
		return !!activity.repeat_end_date && !!req.repeat_end_date && !moment(activity.repeat_end_date).isSame(moment(req.repeat_end_date), 'minute');
	}

	private hasDangerousChanges(): 'full' | 'partial' | 'none' {
		if (this.activityInstances$.value.length === 0) {
			return 'none';
		}

		// Changing entire schedule types - full destruction
		if (this.activity.schedule_type != this.editScheduleForm.value[SCHEDULE_TYPE_KEY]) {
			return 'full';
		}

		// Changing from every flex to on selected days - partial destruction
		if (
			this.activity.schedule_type == 'flex_period' &&
			this.activity.state != this.editScheduleForm.value[FLEX_PERIOD_REPEATS_KEY] &&
			this.editScheduleForm.value[FLEX_PERIOD_REPEATS_KEY] == 'scheduled'
		) {
			return 'partial';
		}

		// Changing flex periods - full destruction
		if (this.activity.schedule_type == 'flex_period' && this.activity.flex_period_id != this.editScheduleForm.value[FLEX_PERIOD_ID_KEY]) {
			return 'full';
		}

		// Same flex period and we were scheduled - no destruction
		if (
			this.activity.schedule_type == 'flex_period' &&
			this.activity.state == 'scheduled' &&
			this.editScheduleForm.value[FLEX_PERIOD_REPEATS_KEY] == 'flex_recurring'
		) {
			const lastInstance = this.activityInstances$.value.sort((a, b) => moment(a.start_time).diff(moment(b.start_time)))[
				this.activityInstances$.value.length - 1
			];
			if (moment(lastInstance.start_time).isAfter(moment(this.editScheduleForm.value[SCHEDULE_REPEAT_END_DATE_KEY]))) {
				return 'partial';
			} else {
				return 'none';
			}
		}

		// Moving end date earlier - partial destruction
		if (
			this.activity.repeat_end_date &&
			moment(this.editScheduleForm.value[SCHEDULE_REPEAT_END_DATE_KEY]).isBefore(moment(this.activity.repeat_end_date))
		) {
			return 'partial';
		}

		// Scheduled time changed - full destruction
		if (
			this.activity.schedule_type == 'custom' &&
			this.editScheduleForm.value[CUSTOM_SCHEDULE_DATE_KEY] &&
			this.editScheduleForm.value[CUSTOM_SCHEDULE_TIME_RANGE_KEY]
		) {
			const tz = this.httpService.currentSchoolSubject.getValue().timezone;
			const { startTime, endTime } = getCustomStartEndTime(
				this.editScheduleForm.value[CUSTOM_SCHEDULE_DATE_KEY],
				this.editScheduleForm.value[CUSTOM_SCHEDULE_TIME_RANGE_KEY],
				tz
			);

			if (!moment(this.activity.custom_start_time).isSame(startTime) || !moment(this.activity.custom_end_time).isSame(endTime)) {
				return 'full';
			}
		}

		// Repeat rule changed - full destruction
		if (
			this.editScheduleForm.value[SCHEDULE_TYPE_KEY] == 'custom' &&
			this.editScheduleForm.value[CUSTOM_SCHEDULE_REPEAT_KEY] != this.activity.custom_repeat_rule
		) {
			if (this.editScheduleForm.value[CUSTOM_SCHEDULE_REPEAT_KEY] == '') {
				return 'full';
			}
			if (this.activity.custom_repeat_rule == '') {
				return 'none';
			}

			// Check if the repeat rule is _MORE_ restrictive
			// This check works because we order the rules weekly -> biweekly -> monthly
			// Changing the rule order will make this check less effective
			const startDate = this.editScheduleForm.value[CUSTOM_SCHEDULE_DATE_KEY];
			if (startDate) {
				const options = getRepeatOptions(startDate);
				const currentIndex = options.findIndex((o) => o.value == this.editScheduleForm.value[CUSTOM_SCHEDULE_REPEAT_KEY]);
				const prevIndex = options.findIndex((o) => o.value == this.activity.custom_repeat_rule);
				if (prevIndex == -1) {
					// The previous repeat rule was not in the list, the schedules are not compatible
					return 'full';
				}
				if (currentIndex > prevIndex) {
					return 'partial';
				}
			}
		}

		return 'none';
	}

	protected confirmScheduleChanges(dismiss: () => void, dismissParent: () => void): void {
		dismiss();
		this.saveScheduleChanges(dismissParent, true);
	}
}
